import type {
  DesignObject,
  DesignTextObject,
  MmPoint,
  MmRect,
  ObjectTransform,
} from '../domain/types';

// Fabric's unstyled line box is 1.13 em high. Keep this in sync with Fabric's
// `_fontSizeMult`; it lets production validation mirror the renderer without a
// DOM/canvas dependency.
const FABRIC_TEXT_LINE_BOX_EM = 1.13;

/**
 * Physical per-line font height after the editor's persisted vertical transform.
 *
 * Straight text follows Fabric's `calcTextHeight`: line height is inserted only
 * between lines and the final line is one 1.13 em box. A curved text object's
 * intrinsic height comes from its arc path instead, so use the circle diameter
 * as a conservative upper bound for that path height. Rotation cannot change
 * this local physical height.
 */
export function effectiveTextHeightMm(object: DesignTextObject): number {
  const lineCount = Math.max(1, object.text.split(/\r\n|\r|\n/).length);
  const lineLayoutEm = 1 + (lineCount - 1) * Math.max(0, object.lineHeight);
  const straightTextHeight =
    object.transform.sizeMm.height / (FABRIC_TEXT_LINE_BOX_EM * lineLayoutEm);

  if (!object.curve || object.curve.radiusMm <= 0) return straightTextHeight;

  const conservativeCurveHeight =
    (object.fontSizeMm * object.transform.sizeMm.height) / (object.curve.radiusMm * 2);
  return Math.min(straightTextHeight, conservativeCurveHeight);
}

export function rotatePoint(point: MmPoint, center: MmPoint, rotationDeg: number): MmPoint {
  if (rotationDeg % 360 === 0) return { ...point };
  const radians = (rotationDeg * Math.PI) / 180;
  const cosine = Math.cos(radians);
  const sine = Math.sin(radians);
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  return {
    x: center.x + dx * cosine - dy * sine,
    y: center.y + dx * sine + dy * cosine,
  };
}

/** Corners of an object whose persisted position is the rotated top-left Fabric origin. */
export function objectCorners(transform: ObjectTransform): MmPoint[] {
  const { positionMm, sizeMm, rotationDeg } = transform;
  const origin = positionMm;
  return [
    { x: positionMm.x, y: positionMm.y },
    { x: positionMm.x + sizeMm.width, y: positionMm.y },
    { x: positionMm.x + sizeMm.width, y: positionMm.y + sizeMm.height },
    { x: positionMm.x, y: positionMm.y + sizeMm.height },
  ].map(point => rotatePoint(point, origin, rotationDeg));
}

export function boundsForPoints(points: MmPoint[]): MmRect {
  if (points.length === 0) return { x: 0, y: 0, width: 0, height: 0 };
  const xs = points.map(point => point.x);
  const ys = points.map(point => point.y);
  const left = Math.min(...xs);
  const right = Math.max(...xs);
  const top = Math.min(...ys);
  const bottom = Math.max(...ys);
  return { x: left, y: top, width: right - left, height: bottom - top };
}

function visualPadding(object: DesignObject): { x: number; y: number } {
  let x = 0;
  let y = 0;
  if ('stroke' in object && object.stroke) {
    x = Math.max(x, object.stroke.widthMm / 2);
    y = Math.max(y, object.stroke.widthMm / 2);
  }
  if (object.type === 'text' && object.shadow) {
    const shadowRadius =
      object.shadow.blurMm + Math.hypot(object.shadow.offsetMm.x, object.shadow.offsetMm.y);
    x = Math.max(x, shadowRadius);
    y = Math.max(y, shadowRadius);
  }
  return { x, y };
}

export function objectBounds(object: DesignObject): MmRect {
  const bounds = boundsForPoints(objectCorners(object.transform));
  const padding = visualPadding(object);
  return {
    x: bounds.x - padding.x,
    y: bounds.y - padding.y,
    width: bounds.width + padding.x * 2,
    height: bounds.height + padding.y * 2,
  };
}

export function rectContainsPoint(rect: MmRect, point: MmPoint, tolerance = 1e-7): boolean {
  return (
    point.x >= rect.x - tolerance &&
    point.y >= rect.y - tolerance &&
    point.x <= rect.x + rect.width + tolerance &&
    point.y <= rect.y + rect.height + tolerance
  );
}

export function rectContainsObject(rect: MmRect, object: DesignObject): boolean {
  const padding = visualPadding(object);
  const paddedRect = {
    x: rect.x + padding.x,
    y: rect.y + padding.y,
    width: rect.width - padding.x * 2,
    height: rect.height - padding.y * 2,
  };
  return (
    paddedRect.width >= 0 &&
    paddedRect.height >= 0 &&
    objectCorners(object.transform).every(point => rectContainsPoint(paddedRect, point))
  );
}

export function unionRects(rects: MmRect[]): MmRect | undefined {
  if (rects.length === 0) return undefined;
  const left = Math.min(...rects.map(rect => rect.x));
  const top = Math.min(...rects.map(rect => rect.y));
  const right = Math.max(...rects.map(rect => rect.x + rect.width));
  const bottom = Math.max(...rects.map(rect => rect.y + rect.height));
  return { x: left, y: top, width: right - left, height: bottom - top };
}
